import { useMemo, useState } from "react"
import dayjs from "dayjs"
import { toast } from "react-toastify"
import type { PaginationState } from "@tanstack/react-table"
import { LinkIcon, Squares2X2Icon } from "@heroicons/react/24/outline"
import { useGetSupplementalFeedUrlMutation } from "~/ui-rtk/api/supplementalFeedApi"

import { brandRoutes } from "~/ui-rtk/constants/routes"
import { Breadcrumbs } from "~/ui-rtk/components/features/dashboards/BrandMediaOptimizationDashboard/components/Breadcrumbs/index"
import { DateRangePicker } from "~/ui-rtk/components/ui/controls"
import { DATE_FORMAT, isValidDate, usePeriod } from "~/ui-rtk/hooks/period"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import { AggregationType } from "~/ui-rtk/constants/charts"
import { Loader } from "~/ui-rtk/components/ui/common"
import { useURLSearchParams } from "~/ui-rtk/hooks/url"
import useUrlColumnFilters from "~/ui-rtk/hooks/url-column-filters"
import useUrlSorting, { TSortingState } from "~/ui-rtk/hooks/url-sorting"
import SortByControl from "~/ui-rtk/components/ui/controls/SortBy/SortBy"
import TableFiltersBar from "~/ui-rtk/components/ui/controls/FiltersBar/TableFiltersBar"
import MultiSelectWithChips, {
  TMultiSelectWithChipsOption,
  TMultiSelectWithChipsOptionGroup,
} from "~/ui-rtk/components/ui/controls/MultiSelectWithChips/MultiSelectWithChips"
import { type TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import TableVisualization from "~/ui-rtk/components/ui/charts/TableVisualization"
import { Toggle } from "~/ui-rtk/components/ui/controls/Toggle"
import type {
  TableVisualizationColumn,
  TMetric,
} from "~/ui-rtk/constants/table-visualization-types"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useDrawer from "~/ui-rtk/hooks/drawer"
import { DRAWER_TYPE } from "~/ui-rtk/constants/drawer"

import useConnect, {
  ALL_METRICS,
  ALWAYS_ATTRIBUTES,
  ALWAYS_METRICS,
  BOOLEAN_ATTRIBUTES,
  CUBE_PREFIX,
  DEFAULT_ATTRIBUTES,
  DEFAULT_METRICS,
  EFFICIENCY_METRICS,
  IMAGE_ATTRIBUTES,
  ITEMS_PER_PAGE,
  MEGA_FEED_ATTRIBUTES_MAP,
  MEGA_FEED_METRICS_MAP,
  MONETARY_ATTRIBUTES,
  MONETARY_METRICS,
  NUMERIC_ATTRIBUTES,
  ORDER_METRICS,
  PERCENTAGE_METRICS,
  REVENUE_METRICS,
  SKU_PREFIX_ATTRIBUTES,
  TRAFFIC_METRICS,
  type TAttributeKey,
  type TMetricsMapKey,
} from "./connect"
import useAdMetrics, {
  AD_METRICS_MAP,
  AD_METRIC_KEYS,
  MONETARY_AD_METRICS,
  type TAdMetricKey,
} from "./hooks/useAdMetrics"
import useShopifySales, {
  MONETARY_SHOPIFY_METRICS,
  SHOPIFY_METRICS_MAP,
  SHOPIFY_METRIC_KEYS,
  type TShopifyMetricKey,
} from "./hooks/useShopifySales"
import useColorways from "./hooks/useColorways"
import { absoluteFeedUrl } from "./feedUrl"
import StructuredFeedDialog, {
  type StructuredFeedProduct,
} from "./StructuredFeed/StructuredFeedDialog"

const CHART_ID = "mega-feed"
const METRICS_URL_KEY = "metrics"
const ATTRIBUTES_URL_KEY = "attributes"
const AD_METRICS_URL_KEY = "ads"

const maxDateRestriction = dayjs().toDate()

const METRIC_GROUPS: TMultiSelectWithChipsOptionGroup[] = [
  { label: "Revenue", value: "revenue" },
  { label: "Orders", value: "orders" },
  { label: "Traffic", value: "traffic" },
  { label: "Efficiency", value: "efficiency" },
]

const getMetricGroup = (metric: TMetricsMapKey) => {
  if (REVENUE_METRICS.includes(metric)) return "revenue"
  if (ORDER_METRICS.includes(metric)) return "orders"
  if (TRAFFIC_METRICS.includes(metric)) return "traffic"
  if (EFFICIENCY_METRICS.includes(metric)) return "efficiency"
  return "revenue"
}

// Column render type. Metrics are money / percent / plain counts; attributes
// are mostly text with a few typed exceptions.
const getMetricType = (metric: TMetricsMapKey) => {
  if (MONETARY_METRICS.includes(metric)) return "currency" as const
  if (PERCENTAGE_METRICS.includes(metric)) return "percent" as const
  return "number" as const
}

const getAttributeType = (attribute: TAttributeKey) => {
  if (IMAGE_ATTRIBUTES.includes(attribute)) return "image" as const
  if (BOOLEAN_ATTRIBUTES.includes(attribute)) return "boolean" as const
  if (MONETARY_ATTRIBUTES.includes(attribute)) return "currency" as const
  if (NUMERIC_ATTRIBUTES.includes(attribute)) return "number" as const
  if (attribute === "parent_title") return "title" as const
  return "text" as const
}

const METRIC_OPTIONS: TMultiSelectWithChipsOption[] = ALL_METRICS.map(
  metric => ({
    label: MEGA_FEED_METRICS_MAP[metric],
    value: metric,
    group: getMetricGroup(metric),
  }),
)

const ATTRIBUTE_OPTIONS: TMultiSelectWithChipsOption[] = (
  Object.keys(MEGA_FEED_ATTRIBUTES_MAP) as TAttributeKey[]
)
  // The always-on identity columns aren't user-removable, so don't offer them.
  .filter(attribute => !ALWAYS_ATTRIBUTES.includes(attribute))
  .map(attribute => ({
    label: MEGA_FEED_ATTRIBUTES_MAP[attribute],
    value: attribute,
  }))

// Attribute filters that aren't necessarily visible as columns — the feed is
// wide, and filtering by e.g. gender shouldn't require showing that column.
const ADDITIONAL_FILTERS = [
  { value: "brand", label: "Brand", type: "text" as const },
  { value: "product_type", label: "Product Type", type: "text" as const },
  {
    value: "google_product_category",
    label: "Google Category",
    type: "text" as const,
  },
  { value: "collections", label: "Collections", type: "text" as const },
  { value: "tags", label: "Tags", type: "text" as const },
  { value: "condition", label: "Condition", type: "text" as const },
  { value: "age_group", label: "Age Group", type: "text" as const },
  { value: "gender", label: "Gender", type: "text" as const },
  { value: "shipping_country", label: "Ships From", type: "text" as const },
  { value: "id_sku", label: "SKU", type: "text" as const },
  ...SKU_PREFIX_ATTRIBUTES.map(attribute => ({
    value: attribute,
    label: MEGA_FEED_ATTRIBUTES_MAP[attribute],
    type: "text" as const,
  })),
  { value: "gtin", label: "GTIN", type: "text" as const },
]

// Trailing-window shortcuts. The view's *_last_7d / *_last_30d measures can't
// be used from this page (Cube would intersect them with the date range below),
// so the equivalent is simply moving the date range.
const DATE_PRESETS: { label: string; days: number }[] = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
]

export default function MegaFeedPage() {
  const { params, setParams } = useURLSearchParams()
  const { setPeriod } = usePeriod()
  const { openDrawer } = useDrawer()

  // Sorted by first-order revenue, not total_revenue: the latter is GA4's own
  // purchase revenue and is empty for tenants that don't send it, which would
  // make the default ranking an arbitrary ordering of zeros.
  const { sorting, setSorting } = useUrlSorting(CHART_ID, [
    { id: "sum_first_order_revs", desc: true },
  ])

  const { columnFilters } = useUrlColumnFilters(CHART_ID)

  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: ITEMS_PER_PAGE,
  })

  const showAdMetrics = params[AD_METRICS_URL_KEY] === "true"

  const { dateRange } = useMinMaxDateRange(
    undefined,
    maxDateRestriction,
    AggregationType.DAILY,
    false,
    undefined,
    "last30Days",
  )

  const selectedMetrics: TMetricsMapKey[] = useMemo(() => {
    const fromParams = params[METRICS_URL_KEY]
      ? (params[METRICS_URL_KEY]?.split(",") as TMetricsMapKey[])
      : []
    return fromParams.length > 0
      ? [...new Set([...ALWAYS_METRICS, ...fromParams])]
      : [...ALWAYS_METRICS, ...DEFAULT_METRICS]
  }, [params])

  const selectedAttributes: TAttributeKey[] = useMemo(() => {
    const fromParams = params[ATTRIBUTES_URL_KEY]
      ? (params[ATTRIBUTES_URL_KEY]?.split(",") as TAttributeKey[])
      : []
    return fromParams.length > 0 ? fromParams : DEFAULT_ATTRIBUTES
  }, [params])

  const { cubeQuery } = useConnect(
    selectedAttributes,
    selectedMetrics,
    dateRange,
    paginationState.pageIndex,
    columnFilters,
    sorting[0],
  )

  const { resultSet, isLoading, error } = useCubeQuery(cubeQuery)

  // Cube returns fully-qualified keys (`mega_feed.total_revenue`);
  // TableVisualization matches rows against the bare column keys.
  const rows = useMemo<TMetric[]>(() => {
    if (!resultSet) return []
    return resultSet.tablePivot().map(row => {
      const stripped: Record<string, unknown> = {}
      Object.keys(row).forEach(key => {
        stripped[key.replace(`${CUBE_PREFIX}.`, "")] = row[key]
      })
      return stripped as TMetric
    })
  }, [resultSet])

  const totalPages = useMemo(
    () => Math.ceil((resultSet?.totalRows() ?? 0) / ITEMS_PER_PAGE),
    [resultSet],
  )

  // Ad spend / brand value comes from a separate cube, fetched only for the
  // handles on screen and merged in below. See useAdMetrics for why this is not
  // joined into the mega_feed view.
  const pageHandles = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .map(row => String(row.handle ?? "").trim())
            .filter(handle => handle.length > 0),
        ),
      ),
    [rows],
  )

  const { adMetricsByHandle } = useAdMetrics({
    handles: pageHandles,
    dateRange,
    enabled: showAdMetrics,
  })

  // Shopify revenue is always fetched — it is the trustworthy revenue figure,
  // unlike the GA4 total_revenue on the view. Keyed by product id.
  const pageProductIds = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .map(row => String(row.item_group_id ?? "").trim())
            .filter(id => id.length > 0),
        ),
      ),
    [rows],
  )

  const { shopifySalesByProductId } = useShopifySales({
    productIds: pageProductIds,
    dateRange,
  })

  // The colorways behind each product (MD-5394), from the variant-grain cube,
  // merged the same way: the table stays one row per product and shows which
  // colors it carries; the drawer is where a color gets its own feed media.
  const { colorwaysByProductId, isAvailable: hasColorways } = useColorways({
    productIds: pageProductIds,
  })

  // What a structured-feed run targets: the products on this page and,
  // where the variant cube knows them, their colorways.
  const [isStructuredFeedOpen, setIsStructuredFeedOpen] = useState(false)
  const structuredFeedProducts = useMemo<StructuredFeedProduct[]>(
    () =>
      rows
        .map(row => {
          const productId = String(row.item_group_id ?? "").trim()
          return {
            productId,
            title: String(row.parent_title ?? productId),
            colors: colorwaysByProductId[productId]?.colors ?? [],
          }
        })
        .filter(product => product.productId.length > 0),
    [rows, colorwaysByProductId],
  )

  const mergedRows = useMemo<TMetric[]>(
    () =>
      rows.map(row => {
        const handle = String(row.handle ?? "").trim()
        const productId = String(row.item_group_id ?? "").trim()
        const colorways = colorwaysByProductId[productId]
        return {
          ...row,
          ...(shopifySalesByProductId[productId] ?? {}),
          ...(colorways
            ? {
                colorways: colorways.colors.join(" · "),
                colorway_count: colorways.colors.length,
              }
            : {}),
          ...(showAdMetrics ? (adMetricsByHandle[handle] ?? {}) : {}),
        }
      }),
    [
      rows,
      adMetricsByHandle,
      shopifySalesByProductId,
      colorwaysByProductId,
      showAdMetrics,
    ],
  )

  const columnConfig: TableVisualizationColumn[] = useMemo(() => {
    const attributeColumns = Array.from(
      new Set([...ALWAYS_ATTRIBUTES, ...selectedAttributes]),
    ).map(attribute => ({
      key: attribute,
      header: MEGA_FEED_ATTRIBUTES_MAP[attribute],
      type: getAttributeType(attribute),
    }))

    const metricColumns = Array.from(
      new Set([...ALWAYS_METRICS, ...selectedMetrics]),
    ).map(metric => ({
      key: metric,
      header: MEGA_FEED_METRICS_MAP[metric],
      type: getMetricType(metric),
    }))

    // Merged-in columns (Shopify sales, ad metrics) come from separate queries,
    // so the server can't order by them. Sorting is disabled rather than left
    // enabled and quietly sorting only the current page.
    const shopifyColumns = SHOPIFY_METRIC_KEYS.map(metric => ({
      key: metric,
      header: SHOPIFY_METRICS_MAP[metric],
      type: MONETARY_SHOPIFY_METRICS.includes(metric as TShopifyMetricKey)
        ? ("currency" as const)
        : ("number" as const),
      disableSorting: true,
    }))

    const adColumns = showAdMetrics
      ? AD_METRIC_KEYS.map(metric => ({
          key: metric,
          header: AD_METRICS_MAP[metric],
          type: MONETARY_AD_METRICS.includes(metric as TAdMetricKey)
            ? ("currency" as const)
            : ("number" as const),
          disableSorting: true,
        }))
      : []

    // Colorways come from the variant cube, merged per page like the Shopify
    // columns — so not sortable server-side either. Hidden for a tenant whose
    // snapshot has no variant table.
    const colorwayColumns: TableVisualizationColumn[] = hasColorways
      ? [
          {
            key: "colorway_count",
            header: "Colors",
            type: "number" as const,
            disableSorting: true,
          },
          {
            key: "colorways",
            header: "Colorways",
            type: "text" as const,
            disableSorting: true,
          },
        ]
      : []

    return [
      ...attributeColumns,
      ...colorwayColumns,
      ...shopifyColumns,
      ...metricColumns,
      ...adColumns,
    ]
  }, [selectedAttributes, selectedMetrics, showAdMetrics, hasColorways])

  const selectedMetricsOptions = useMemo(
    () =>
      selectedMetrics.map(metric => ({
        label: MEGA_FEED_METRICS_MAP[metric],
        value: metric,
        group: getMetricGroup(metric),
      })),
    [selectedMetrics],
  )

  const selectedAttributesOptions = useMemo(
    () =>
      selectedAttributes.map(attribute => ({
        label: MEGA_FEED_ATTRIBUTES_MAP[attribute],
        value: attribute,
      })),
    [selectedAttributes],
  )

  const resetToFirstPage = () =>
    setPaginationState(state => ({ ...state, pageIndex: 0 }))

  const changePeriod = ({ start, end }: { start: Date; end: Date }) => {
    void setPeriod(
      dayjs(start).format(DATE_FORMAT),
      dayjs(end).format(DATE_FORMAT),
    )
    resetToFirstPage()
  }

  const applyDatePreset = (days: number) =>
    changePeriod({
      start: dayjs().subtract(days, "day").startOf("day").toDate(),
      end: dayjs().subtract(1, "day").endOf("day").toDate(),
    })

  // The grid only carries the columns the user selected; the drawer re-queries
  // the full feed row (long copy, every image slot and array) by product id.
  const handleRowClick = (row: TMetric) => {
    const itemGroupId = String(row.item_group_id ?? "")
    if (!itemGroupId) return

    openDrawer({
      type: DRAWER_TYPE.ProductDetails,
      props: {
        itemGroupId,
        title: String(row.parent_title ?? ""),
        dateRange: [
          dayjs(dateRange[0]).format(DATE_FORMAT),
          dayjs(dateRange[1]).format(DATE_FORMAT),
        ],
      },
      clickSourceId: CHART_ID,
    })
  }

  const handleSort = (sort: TSortingState) => {
    if (
      Object.prototype.hasOwnProperty.call(sort, "desc") &&
      Object.prototype.hasOwnProperty.call(sort, "id")
    ) {
      setSorting([sort])
      resetToFirstPage()
    }
  }

  const handleMetricsChange = (options: TMultiSelectOption[]) => {
    setParams({
      [METRICS_URL_KEY]: options.map(({ value }) => value).join(","),
    })
    resetToFirstPage()
  }

  const handleAttributesChange = (options: TMultiSelectOption[]) => {
    setParams({
      [ATTRIBUTES_URL_KEY]: options.map(({ value }) => value).join(","),
    })
    resetToFirstPage()
  }

  // "Feed URL" button: fetch (minting the token on first use) and copy the
  // company's public supplemental-feed CSV URL for Meta Commerce Manager.
  const [getSupplementalFeedUrl, { isLoading: isFetchingFeedUrl }] =
    useGetSupplementalFeedUrlMutation()
  const handleCopyFeedUrl = async () => {
    try {
      const { url } = await getSupplementalFeedUrl().unwrap()
      await navigator.clipboard.writeText(absoluteFeedUrl(url))
      toast.success(
        "Supplemental feed URL copied — paste it into Meta Commerce Manager as a supplementary data source.",
      )
    } catch {
      toast.error("Couldn't get the feed URL — try again.")
    }
  }

  return (
    <div className="mega-feed-page h-full overflow-y-visible px-system-md">
      <Breadcrumbs
        slug={brandRoutes.MEGA_FEED.ROOT}
        title="Mega Feed"
        parent={{ slug: brandRoutes.MEGA_FEED.ROOT, title: "Mega Feed" }}
      />

      <div className="mt-6 flex justify-between items-center gap-system-sm flex-wrap">
        <div className="flex items-stretch gap-system-xs flex-shrink-0">
          {DATE_PRESETS.map(({ label, days }) => (
            <button
              key={label}
              onClick={() => applyDatePreset(days)}
              className="btn btn-sm bg-system-blue-700 border border-system-blue-600 text-white rounded-system-md hover:bg-system-blue-600"
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex items-stretch gap-system-sm flex-shrink-0">
          <button
            type="button"
            onClick={() => void handleCopyFeedUrl()}
            disabled={isFetchingFeedUrl}
            title="Copy this company's Meta supplemental-feed URL (AdForge custom creatives per product)"
            className="btn btn-sm h-system-2xl bg-system-blue-700 border border-system-blue-600 text-white rounded-system-md hover:bg-system-blue-600 disabled:opacity-60"
          >
            {isFetchingFeedUrl ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <LinkIcon className="w-4 h-4" />
            )}
            {isFetchingFeedUrl ? "Fetching…" : "Feed URL"}
          </button>
          <button
            type="button"
            onClick={() => setIsStructuredFeedOpen(true)}
            title="Apply a shot structure (hero, lifestyle, detail…) to this page's products and colorways"
            className="btn btn-sm h-system-2xl bg-system-blue-700 border border-system-blue-600 text-white rounded-system-md hover:bg-system-blue-600"
          >
            <Squares2X2Icon className="w-4 h-4" />
            Structured feed
          </button>
          <div className="flex items-center gap-system-xs px-system-sm py-system-xxs text-sm rounded-system-md border border-system-blue-600 bg-system-blue-700 h-system-2xl">
            <span className="text-white whitespace-nowrap">Ad metrics</span>
            <Toggle
              size="xs"
              value={showAdMetrics}
              onChange={value =>
                setParams({ [AD_METRICS_URL_KEY]: value ? "true" : undefined })
              }
            />
          </div>
          <TableFiltersBar
            id={CHART_ID}
            columnConfig={columnConfig}
            additionalFilters={ADDITIONAL_FILTERS}
          />
          <SortByControl
            columnConfig={columnConfig}
            sortBy={sorting[0]}
            onSort={handleSort}
          />
          {dateRange?.[0] &&
            dateRange?.[1] &&
            isValidDate(dateRange[0]) &&
            isValidDate(dateRange[1]) && (
              <DateRangePicker
                start={dateRange[0]}
                end={dateRange[1]}
                comparisonEnabled={false}
                maxDate={maxDateRestriction}
                onChange={changePeriod}
              />
            )}
        </div>
      </div>

      <div className="flex items-stretch w-full mt-system-sm gap-system-sm flex-wrap">
        <MultiSelectWithChips
          label="Attributes"
          options={ATTRIBUTE_OPTIONS}
          className="h-system-2xl cursor-pointer"
          showTypeAhead={true}
          value={selectedAttributesOptions}
          onChange={handleAttributesChange}
        />
        <MultiSelectWithChips
          label="Metrics"
          options={METRIC_OPTIONS}
          className="h-system-2xl cursor-pointer"
          groups={METRIC_GROUPS}
          showTypeAhead={true}
          value={selectedMetricsOptions}
          onChange={handleMetricsChange}
        />
      </div>

      {error && (
        <div className="mt-6 p-4 bg-error/10 border border-error rounded-lg text-error">
          {error.toString()}
        </div>
      )}

      {isLoading && rows.length === 0 ? (
        <div className="mt-6 flex justify-center">
          <Loader />
        </div>
      ) : (
        <div className="mt-system-sm">
          <TableVisualization
            metrics={mergedRows}
            columnConfig={columnConfig}
            paginationState={paginationState}
            setPaginationState={setPaginationState}
            totalPages={totalPages}
            enablePagination
            manualPagination
            chartId={CHART_ID}
            dateRange={dateRange}
            onRowClick={handleRowClick}
          />
        </div>
      )}

      <StructuredFeedDialog
        isOpen={isStructuredFeedOpen}
        onClose={() => setIsStructuredFeedOpen(false)}
        products={structuredFeedProducts}
      />
    </div>
  )
}
